import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useTeams } from "@/contexts/teams-provider";
import { useUser } from "@/contexts/user-provider";
import { useUsers } from "@/contexts/users-provider";
import { useApi } from "@/hooks/useApi";
import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";
import { CheckIcon } from "@radix-ui/react-icons";
import { EmployeeDTO, UserDTO } from "@shaarp/common";
import { UserRoundPlusIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { useToastError } from "../ui/use-toast";

export default function EmployeeUserAssignedSelector({
  employee,
  selectedUser,
  onSelectedUserChange,
  onChange = () => {},
}: {
  employee: EmployeeDTO;
  selectedUser?: UserDTO | null;
  onSelectedUserChange?: (user: UserDTO | null) => void;
  onChange?: (newUserId: string | null) => void;
}) {
  const api = useApi();
  const { t } = useTranslation();
  const toastError = useToastError();

  const { isUser } = useTeams();
  const { users, apiFetchUsers } = useUsers();
  const { teamUser } = useUser();

  const [open, setOpen] = useState(false);
  const [localUserSelected, setLocalUserSelected] = useState<UserDTO | null>(
    null,
  );
  const [usersOptions, setUsersOptions] = useState<UserDTO[]>([]);

  const isControlled = selectedUser !== undefined;
  const userSelected = isControlled ? selectedUser : localUserSelected;

  const hasPermissionAssignOther = () => {
    return !isUser;
  };

  const assign = async (newUser: UserDTO | null) => {
    setOpen(false);
    if (isControlled) {
      onSelectedUserChange?.(newUser);
    } else {
      setLocalUserSelected(newUser);
    }
    onChange(newUser ? newUser.id : null);
    try {
      await api.put(`/employee/${employee.id}/assign`, {
        userId: newUser ? newUser.id : "",
      });
    } catch (e) {
      toastError();
    }
  };

  useEffect(() => {
    const init = async () => {
      await apiFetchUsers();
    };
    init();
  }, []);

  useEffect(() => {
    const resolved =
      users.find((u) => u.id === employee.assignation?.userId) || null;
    if (isControlled) {
      onSelectedUserChange?.(resolved);
    } else {
      setLocalUserSelected(resolved);
    }
    setUsersOptions(users.filter((u) => u.id !== teamUser?.id));
  }, [employee.assignation?.userId, teamUser, users]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Avatar
              className={cn(
                "w-10 h-10 cursor-pointer border",
                userSelected ? "border-primary" : "border-border",
              )}
            >
              <AvatarImage
                src={userSelected?.imageUrl}
                alt={userSelected?.fullName}
              />
              <AvatarFallback>
                {userSelected ? (
                  userSelected.fullName
                ) : (
                  <UserRoundPlusIcon className="w-5 h-5 text-muted-foreground" />
                )}
              </AvatarFallback>
            </Avatar>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent side="top">
          {userSelected
            ? t("employee.userAssignedSelector.assignedTo", {
                name: userSelected.fullName,
              })
            : t("employee.userAssignedSelector.notAssigned")}
        </TooltipContent>
      </Tooltip>
      <PopoverContent className="p-0" side="bottom" align="center">
        <Command>
          <CommandInput
            placeholder={t("employee.userAssignedSelector.search")}
          />
          <CommandList>
            {teamUser && (
              <>
                <CommandGroup>
                  <CommandItem
                    key={teamUser.fullName}
                    value={teamUser.fullName || ""}
                    disabled={false}
                    onSelect={() => assign(teamUser)}
                  >
                    <Avatar className="mr-3 w-6 h-6">
                      <AvatarImage
                        src={teamUser.imageUrl}
                        alt={teamUser.fullName}
                        className="object-cover"
                      />
                      <AvatarFallback>{teamUser.fullName}</AvatarFallback>
                    </Avatar>
                    {t("employee.userAssignedSelector.assignToMe")}
                    <CheckIcon
                      className={cn(
                        "ml-auto h-4 w-4",
                        teamUser.id === userSelected?.id
                          ? "opacity-100"
                          : "opacity-0",
                      )}
                    />
                  </CommandItem>
                </CommandGroup>
                <CommandSeparator />
              </>
            )}
            <CommandGroup>
              <CommandItem
                key={"Non assigné"}
                value={"Non assigné"}
                onSelect={() => assign(null)}
              >
                <Avatar className="mr-3 w-6 h-6">
                  <AvatarFallback>
                    {t("employee.userAssignedSelector.notAssignedAbbr")}
                  </AvatarFallback>
                </Avatar>
                {t("employee.userAssignedSelector.notAssigned")}
                <CheckIcon
                  className={cn(
                    "ml-auto w-4 h-4",
                    !userSelected ? "opacity-100" : "opacity-0",
                  )}
                />
              </CommandItem>
            </CommandGroup>
            {hasPermissionAssignOther() && usersOptions.length > 0 && (
              <>
                <CommandSeparator />
                <CommandGroup>
                  {users
                    .filter((u) => u.id !== teamUser?.id)
                    .map((u) => (
                      <CommandItem
                        key={u.fullName}
                        value={u.fullName}
                        disabled={false}
                        onSelect={() => assign(u)}
                      >
                        <Avatar className="mr-3 w-6 h-6">
                          <AvatarImage src={u.imageUrl} alt={u.fullName} />
                          <AvatarFallback>{u.fullName}</AvatarFallback>
                        </Avatar>
                        {u.fullName}
                        <CheckIcon
                          className={cn(
                            "ml-auto h-4 w-4",
                            u.id === userSelected?.id
                              ? "opacity-100"
                              : "opacity-0",
                          )}
                        />
                      </CommandItem>
                    ))}
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
